import api from '@/utils/api'

/**
 * Get WebAuthn status (whether user has any registered security keys)
 */
export function getWebAuthnStatus(options) {
  return api.post('/webauthn/status', {}, options)
}

/**
 * Begin registration - get challenge from server
 */
export function beginRegistration(options) {
  return api.post('/webauthn/register/begin', {}, options)
}

/**
 * Finish registration - send attestation to server
 */
export function finishRegistration(params, options) {
  return api.post('/webauthn/register/finish', params, options)
}

/**
 * Begin authentication - get challenge from server
 */
export function beginAuthentication(params, options) {
  return api.post('/webauthn/authenticate/begin', params, options)
}

/**
 * Finish authentication - send assertion to server
 */
export function finishAuthentication(params, options) {
  return api.post('/webauthn/authenticate/finish', params, options)
}

/**
 * List registered credentials
 */
export function listCredentials(options) {
  return api.post('/webauthn/credentials', {}, options)
}

/**
 * Delete a credential
 */
export function deleteCredential(params, options) {
  return api.post('/webauthn/credentials/delete', params, options)
}

// --- WebAuthn Browser API Helpers ---

/**
 * Check if WebAuthn is supported in the browser
 */
export function isWebAuthnSupported() {
  return (
    window.PublicKeyCredential !== undefined &&
    typeof window.PublicKeyCredential === 'function'
  )
}

/**
 * Convert base64url string to ArrayBuffer
 */
function base64urlToBuffer(base64url) {
  // Add padding if needed
  let base64 = base64url.replace(/-/g, '+').replace(/_/g, '/')
  while (base64.length % 4 !== 0) {
    base64 += '='
  }
  const binary = atob(base64)
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i)
  }
  return bytes.buffer
}

/**
 * Convert ArrayBuffer to base64url string
 */
function bufferToBase64url(buffer) {
  const bytes = new Uint8Array(buffer)
  let binary = ''
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i])
  }
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '')
}

/**
 * Create a new credential (registration)
 * @param {object} serverOptions - The publicKey options from the server
 * @returns {Promise<object>} The credential response to send back to the server
 */
export async function createCredential(serverOptions) {
  const publicKey = serverOptions.publicKey

  // Convert challenge from base64url to ArrayBuffer
  publicKey.challenge = base64urlToBuffer(publicKey.challenge)

  // Convert user.id from base64url to ArrayBuffer
  publicKey.user.id = base64urlToBuffer(publicKey.user.id)

  // Convert excludeCredentials IDs
  if (publicKey.excludeCredentials) {
    publicKey.excludeCredentials = publicKey.excludeCredentials.map((cred) => ({
      ...cred,
      id: base64urlToBuffer(cred.id),
    }))
  }

  // Call the browser WebAuthn API
  const credential = await navigator.credentials.create({ publicKey })

  // Convert the response to JSON-serializable format
  return {
    id: credential.id,
    rawId: bufferToBase64url(credential.rawId),
    type: credential.type,
    response: {
      attestationObject: bufferToBase64url(
        credential.response.attestationObject
      ),
      clientDataJSON: bufferToBase64url(credential.response.clientDataJSON),
    },
  }
}

/**
 * Get an assertion (authentication)
 * @param {object} serverOptions - The publicKey options from the server
 * @returns {Promise<object>} The assertion response to send back to the server
 */
export async function getAssertion(serverOptions) {
  const publicKey = serverOptions.publicKey

  // Convert challenge from base64url to ArrayBuffer
  publicKey.challenge = base64urlToBuffer(publicKey.challenge)

  // Convert allowCredentials IDs
  if (publicKey.allowCredentials) {
    publicKey.allowCredentials = publicKey.allowCredentials.map((cred) => ({
      ...cred,
      id: base64urlToBuffer(cred.id),
    }))
  }

  // Call the browser WebAuthn API
  const assertion = await navigator.credentials.get({ publicKey })

  // Convert the response to JSON-serializable format
  return {
    id: assertion.id,
    rawId: bufferToBase64url(assertion.rawId),
    type: assertion.type,
    response: {
      authenticatorData: bufferToBase64url(
        assertion.response.authenticatorData
      ),
      clientDataJSON: bufferToBase64url(assertion.response.clientDataJSON),
      signature: bufferToBase64url(assertion.response.signature),
      userHandle: assertion.response.userHandle
        ? bufferToBase64url(assertion.response.userHandle)
        : undefined,
    },
  }
}
